import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirProposalFullType, DocumentDirectoryType, ProposalExtraItemType, ProposalProductItemType, ProposalSaveDataType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import ESpinner from 'components/others/Spinner';
import { useTemplateProductsMap } from '../share/model/useTemplateProductsMap';
import { buildDefaultDocumentMeta } from '../share/model/buildDefaultDocumentMeta';
import DocumentEditorForm, { type DocumentEditorMode } from './DocumentEditorForm';
import type { ProposalEditFormValues } from '../Proposal/types';

export type { DocumentEditorMode };

export interface DocumentItemsOverride {
    products?: ProposalProductItemType[];
    additionalItems?: ProposalExtraItemType[];
    pricesInUsd?: boolean;
    proposalVat?: boolean;
}

export interface DocumentItemsAppend {
    products?: ProposalProductItemType[];
    additionalItems?: ProposalExtraItemType[];
}

interface DocumentEditorOffcanvasProps {
    mode?: DocumentEditorMode;
    objectData?: unknown;
    objectId?: number | string;
    objectName?: string;
    clientEmail?: string;
    /** Default document type when creating without a template. */
    defaultDocumentType?: DocumentDirectoryType;
    /** When creating (documentId === 0), preload form from this directory template. */
    templateDirId?: number | null;
    /** Replace products/services after template load (e.g. from accepted commercial proposal). */
    itemsOverride?: DocumentItemsOverride | null;
    /** Append products/services after template load (e.g. object materials/expenses). */
    itemsAppend?: DocumentItemsAppend | null;
    documentId: number | null;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
    onGenerated?: () => Promise<void> | void;
}

const createEmptyData = (
    proposalType: DocumentDirectoryType,
    usdDefault = false,
    vatDefault = false,
): ProposalEditFormValues => ({
    proposalType,
    proposalName: '',
    proposalComment: '',
    proposalNumber: '',
    proposalDt: null,
    proposalLogo: '',
    proposalBackground: '',
    proposalTitle: '',
    proposalIntro: '',
    proposalOutro: '',
    pricesInUsd: Boolean(usdDefault),
    proposalVat: Boolean(vatDefault),
    proposalTitlePage: false,
    products: [],
    additionalItems: [],
});

const mapDirToForm = (data: DirProposalFullType, fallbackType: DocumentDirectoryType): ProposalEditFormValues => ({
    proposalType: (data.proposalType && data.proposalType !== 'proposal'
        ? data.proposalType
        : fallbackType) as DocumentDirectoryType,
    proposalName: String(data.proposalName || ''),
    proposalComment: String(data.proposalComment || ''),
    proposalNumber: '',
    proposalDt: null,
    proposalLogo: String(data.proposalLogo || ''),
    proposalBackground: String(data.proposalBackground || ''),
    proposalTitle: String(data.proposalTitle || ''),
    proposalIntro: String(data.proposalIntro || ''),
    proposalOutro: String(data.proposalOutro || ''),
    pricesInUsd: Boolean(data.pricesInUsd),
    proposalVat: Boolean(data.proposalVat),
    proposalTitlePage: false,
    products: Array.isArray(data.products) ? data.products : [],
    additionalItems: Array.isArray(data.additionalItems) ? data.additionalItems : [],
});

const applyItemsOverride = (
    base: ProposalEditFormValues,
    itemsOverride?: DocumentItemsOverride | null,
): ProposalEditFormValues => {
    if (!itemsOverride) return base;
    return {
        ...base,
        products: Array.isArray(itemsOverride.products) ? itemsOverride.products : [],
        additionalItems: Array.isArray(itemsOverride.additionalItems) ? itemsOverride.additionalItems : [],
        pricesInUsd:
            typeof itemsOverride.pricesInUsd === 'boolean' ? itemsOverride.pricesInUsd : base.pricesInUsd,
        proposalVat:
            typeof itemsOverride.proposalVat === 'boolean' ? itemsOverride.proposalVat : base.proposalVat,
    };
};

const applyItemsAppend = (
    base: ProposalEditFormValues,
    itemsAppend?: DocumentItemsAppend | null,
): ProposalEditFormValues => {
    if (!itemsAppend) return base;
    const appendProducts = Array.isArray(itemsAppend.products) ? itemsAppend.products : [];
    const appendAdditional = Array.isArray(itemsAppend.additionalItems) ? itemsAppend.additionalItems : [];
    if (!appendProducts.length && !appendAdditional.length) return base;
    return {
        ...base,
        products: [...(base.products || []), ...appendProducts],
        additionalItems: [...(base.additionalItems || []), ...appendAdditional],
    };
};

const DocumentEditorOffcanvas = ({
    mode = 'dir',
    objectData,
    objectId,
    objectName = '',
    clientEmail = '',
    defaultDocumentType = 'contract',
    templateDirId = null,
    itemsOverride = null,
    itemsAppend = null,
    documentId,
    onClose,
    onSaved,
    onGenerated,
}: DocumentEditorOffcanvasProps) => {
    const { t } = useTranslation(['Document', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const { productsMap, loading: loadingProducts } = useTemplateProductsMap();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const emptyData = useMemo(
        () => createEmptyData(defaultDocumentType, params.usdDefault, params.vatDefault),
        [defaultDocumentType, params.usdDefault, params.vatDefault],
    );
    const [formData, setFormData] = useState<ProposalEditFormValues>(() =>
        createEmptyData(defaultDocumentType, params.usdDefault, params.vatDefault),
    );
    const sourceDocumentId = documentId != null && documentId < 0 ? Math.abs(documentId) : null;
    const isCreate = documentId === 0 || sourceDocumentId != null;
    const isObjectMode = mode !== 'dir';

    const title = useMemo(() => {
        if (isObjectMode) return t('edit.titleGenerate');
        return isCreate ? t('edit.titleCreate') : t('edit.titleUpdate');
    }, [isCreate, isObjectMode, t]);

    const toPayload = (payload: ProposalEditFormValues): ProposalSaveDataType => ({
        proposalType: payload.proposalType,
        proposalName: payload.proposalName,
        proposalComment: payload.proposalComment,
        proposalLogo: payload.proposalLogo,
        proposalBackground: payload.proposalBackground,
        proposalTitle: payload.proposalTitle,
        proposalIntro: payload.proposalIntro,
        proposalOutro: payload.proposalOutro,
        pricesInUsd: payload.pricesInUsd,
        proposalVat: payload.proposalVat,
        proposalTitlePage: false,
        products: payload.products,
        additionalItems: payload.additionalItems,
    });

    const withDuplicateName = (data: ProposalEditFormValues): ProposalEditFormValues => {
        const baseName = String(data.proposalName || '').trim();
        const suffix = t('card.duplicateNameSuffix');
        if (!baseName) return { ...data, proposalName: suffix.trim() };
        if (baseName.endsWith(suffix.trim())) return data;
        return { ...data, proposalName: `${baseName}${suffix}` };
    };

    const resolveObjectDocumentMeta = async (): Promise<Pick<ProposalEditFormValues, 'proposalNumber' | 'proposalDt'>> => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            return { proposalNumber: '', proposalDt: null };
        }
        const rows = await apiDocument.getObjectDocumentGenerations(targetObjectId);
        const existingCount = (rows || []).filter(
            (row) => (row.source || 'object') === 'object' && !Number(row.deleted || 0),
        ).length;
        return buildDefaultDocumentMeta({
            entityId: targetObjectId,
            sequence: existingCount + 1,
            letter: t('edit.fields.documentNumberLetterObject'),
        });
    };

    const fetchData = async () => {
        if (documentId === null) return;

        if (isCreate || isObjectMode) {
            const preloadId = sourceDocumentId || templateDirId || null;
            setLoadingData(true);
            try {
                let base: ProposalEditFormValues = emptyData;
                if (preloadId) {
                    const data = await apiDocument.getDirDocument(preloadId);
                    const mapped = data ? mapDirToForm(data, defaultDocumentType) : emptyData;
                    base = sourceDocumentId ? withDuplicateName(mapped) : mapped;
                }
                const withItems = applyItemsAppend(applyItemsOverride(base, itemsOverride), itemsAppend);
                if (isObjectMode) {
                    const meta = await resolveObjectDocumentMeta();
                    setFormData({ ...withItems, ...meta });
                } else {
                    setFormData(withItems);
                }
            } finally {
                setLoadingData(false);
            }
            return;
        }

        setLoadingData(true);
        const data = await apiDocument.getDirDocument(documentId);
        setLoadingData(false);
        setFormData(data ? mapDirToForm(data, defaultDocumentType) : emptyData);
    };

    const handleSubmit = async (payload: ProposalEditFormValues) => {
        if (documentId === null || isObjectMode) return;
        setLoading(true);

        let stat = false;
        if (isCreate) {
            const createdId = await apiDocument.createDirDocument(toPayload(payload));
            stat = Boolean(createdId);
        } else {
            stat = await apiDocument.saveDirDocument(documentId, toPayload(payload));
        }
        setLoading(false);

        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        if (isCreate) {
            showToast(t('messCreated'), 'success');
            await onSaved();
            onClose();
            return;
        }

        showToast(t('messSaved'), 'success');
        await onSaved();
    };

    useEffect(() => {
        if (documentId === null) return;
        void fetchData();
    }, [documentId, templateDirId, defaultDocumentType, mode, itemsOverride, itemsAppend]);

    return (
        <Offcanvas show={documentId !== null} onHide={onClose} placement="end" className="off-import">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="proposal-editor-offcanvas-body position-relative pb-0 d-flex flex-column px-0">
                {loadingData || loadingProducts ? (
                    <ESpinner />
                ) : (
                    <DocumentEditorForm
                        key={`document-${mode}-${documentId || 0}-${templateDirId || 0}-${defaultDocumentType}-${itemsOverride ? 'seed' : 'plain'}-${itemsAppend ? 'append' : 'noappend'}`}
                        mode={mode}
                        objectData={objectData}
                        objectId={objectId}
                        objectName={objectName}
                        clientEmail={clientEmail}
                        dirProposalId={templateDirId}
                        initialValues={formData}
                        loading={loading}
                        submitLabel={isCreate ? t('common:buttons.create') : t('common:buttons.save')}
                        productsMap={productsMap}
                        rate={rate}
                        onSubmit={handleSubmit}
                        onGenerated={async () => {
                            if (onGenerated) await onGenerated();
                            onClose();
                        }}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default DocumentEditorOffcanvas;
